import { useState, useRef, useEffect } from 'react';
import { getWithExpiry, setWithExpiry } from '../utils/sessionStorage';
import { isUserFirefox } from '../utils/firefoxUtils';
import { isUserSafari } from '../utils/safariUtils';

export const useCamera = () => {
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  // Initialize with stored device ID if available
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>(
    getWithExpiry('selectedCameraDeviceId') || ''
  );

  const [deviceRemoved, setDeviceRemoved] = useState(false);

   // Get available camera devices
  const getVideoDevices = async () => {
    try {
      setError(null);
      
      try {
        await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        
        // Safari might need a small delay
        if (isUserSafari()) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      } catch (error) {
        console.log('Permission request failed, trying video only:', error);
        setError(`Could not access camera. Please check your browser permissions and try again. (${Date.now()})`);
        // Fallback for Safari - try video only
        await navigator.mediaDevices.getUserMedia({ video: true });
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');

      // Check if the currently selected device was removed
      const currentDeviceExists = videoDevices.some(device => device.deviceId === selectedDeviceId);
      if (selectedDeviceId && !currentDeviceExists && isCameraOn) {
        console.log('Current camera device was removed during active session');
        setDeviceRemoved(true);
        setError(`Your camera device has been disconnected. Please reconnect your camera or select a different device. (${Date.now()})`);
        // Stop the camera since the device is no longer available
        stopCamera();
      } else if (deviceRemoved && currentDeviceExists) {
        // Device was reconnected
        setDeviceRemoved(false);
        setError(null);
      }
      
      // Only update if devices actually changed
      const devicesChanged = JSON.stringify(videoDevices.map(d => d.deviceId)) !== 
                            JSON.stringify(devices.map(d => d.deviceId));
      
      if (devicesChanged || devices.length === 0) {
        console.log('Video devices updated:', videoDevices.length);
        setDevices(videoDevices);
      }
      
      return videoDevices;
    } catch (err) {
      console.error('Failed to get video devices:', err);
      setError(`Could not access camera devices. Please check your browser permissions and try again. (${Date.now()})`);
      return [];
    }
  };

  // Effect to handle device selection when devices change
  useEffect(() => {
    if (devices.length > 0) {
      // Get stored device ID if available
      const storedDeviceId = getWithExpiry('selectedCameraDeviceId');
      
      // Check if we need to set a default device
      if (!storedDeviceId) {
        // No stored selection, use first device as default and save it
        const defaultDeviceId = devices[0].deviceId;
        setSelectedDeviceId(defaultDeviceId);
        setWithExpiry('selectedCameraDeviceId', defaultDeviceId, 24 * 60 * 60 * 1000); // 24 hours
        console.log('Set default camera device:', defaultDeviceId);
      } else {
        // Check if the stored device is still available
        const deviceExists = devices.some(device => device.deviceId === storedDeviceId);
        
        if (deviceExists) {
          // Use the stored device
          setSelectedDeviceId(storedDeviceId);
        } else {
          // Stored device not available, use first device instead
          const newDefaultId = devices[0].deviceId;
          setSelectedDeviceId(newDefaultId);
          setWithExpiry('selectedCameraDeviceId', newDefaultId, 24 * 60 * 60 * 1000); // 24 hours
          console.log('Stored camera not found, using new default:', newDefaultId);

          // If camera was on when device was removed, show error
          if (isCameraOn) {
            setDeviceRemoved(true);
            setError(`Your camera device has been disconnected. Please reconnect your camera or select a different device. (${Date.now()})`);
            stopCamera();
          }
        }
      }
    }
  }, [devices]);

  // Load devices on initialization and listen for device changes
  useEffect(() => {
    // Initial load
    getVideoDevices();

    // Listen for device changes (plugging/unplugging devices)
    const handleDeviceChange = () => {
      console.log('Device change detected, refreshing video devices...');
      getVideoDevices();
    };

    // Add event listener for device changes
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    // Cleanup event listener on unmount
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, []);

  // This effect ensures the video element gets the stream once both are available
  useEffect(() => {
    if (isCameraOn && streamRef.current && videoRef.current) {
      // console.log("Setting video srcObject in effect");
      videoRef.current.srcObject = streamRef.current;
    }
  }, [isCameraOn]);

  // Monitor media stream tracks for unexpected endings
  useEffect(() => {
    if (streamRef.current && isCameraOn) {
      const videoTracks = streamRef.current.getVideoTracks();
      
      const handleTrackEnded = (event: Event) => {
        console.log('Video track ended unexpectedly:', event);
        const track = event.target as MediaStreamTrack;
        
        // Check if this was an unexpected ending (not initiated by us)
        if (track.readyState === 'ended') {
          console.log('Camera track ended unexpectedly - device may have been removed');
          setDeviceRemoved(true);
          setError(`Your camera device has been disconnected. Please reconnect your camera or select a different device. (${Date.now()})`);
          setIsCameraOn(false);
        }
      };

      videoTracks.forEach(track => {
        track.addEventListener('ended', handleTrackEnded);
      });

      return () => {
        videoTracks.forEach(track => {
          track.removeEventListener('ended', handleTrackEnded);
        });
      };
    }
  }, [streamRef.current, isCameraOn]);

  const startCamera = async (deviceId?: string) => {
    try {
      setError(null);
      console.log('Starting camera with deviceId:', deviceId);
      setIsLoading(true);
      setDeviceRemoved(false);

      const isAndroid = /android/i.test(navigator.userAgent);
      console.log('isAndroid:', isAndroid);

      // Use provided deviceId, or fallback to selected device or true
      const constraints = {
        video: isAndroid ? 
        {
          facingMode: "user",
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 30 }
        } : deviceId ? { deviceId: { exact: deviceId } } : 
              selectedDeviceId ? { deviceId: { exact: selectedDeviceId } } : true
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      if (deviceId) {
        setSelectedDeviceId(deviceId || '');
      }

      streamRef.current = stream;
      setIsCameraOn(true);

      videoRef.current!.srcObject = stream;


      return stream;
    } catch (err) {
      console.error('Failed to start camera:', err);
      setError(`Could not access camera. Please check your browser permissions and try again. (${Date.now()})`);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const stopCamera = () => {
    setError(null);
    setDeviceRemoved(false);
    // console.log('Stopping camera');
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setIsCameraOn(false);
  };

  const toggleCamera = async (deviceId?: string) => {
    // console.log('Toggle camera, current state:', isCameraOn);
    try{
      setError(null);
      if (isCameraOn) {
        stopCamera();
      } else {
        await startCamera(deviceId || selectedDeviceId);
      }
    } catch (err) {
      console.error('Failed to toggle camera:', err);
      setError(`Could not access camera. Please check your browser permissions and try again. (${Date.now()})`);
      return null;
    }
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        stopCamera();
      }
    };
  }, []);

  return {
    isCameraOn,
    isLoading,
    error,
    videoRef,
    toggleCamera,
    setIsCameraOn,
    devices,
    selectedDeviceId,
    setSelectedDeviceId,
    startCamera,
    getVideoDevices,
    deviceRemoved,
    setDeviceRemoved,
  };
};

export default useCamera;
